import { useMutation } from '@apollo/client';
import {
  AdminUpdateUserMutation,
  AdminUpdateUserMutationVariables,
  MutationSetCollaboratorsArgs,
  User,
  UserUpdateInput,
} from 'lib/generated/graphql';
import { ADMIN_UPDATE_USER_MUTATION, SEND_COLLABORATOR_EMAILS, SET_COLLABORATORS_MUTATION } from 'lib/mutations';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import useAuth from 'src/shared-components/Auth';
import Modal from 'src/shared-components/Modal';
import CollaboratorsForm from './components/CollaboratorsForm';
import SkillsForm from './components/SkillsForm';

interface SkillsAndCollaboratorsProps {
  user: User;
  onUserUpdated: () => void;
  setIsLoading?: (isLoading: boolean) => void;
}
const SkillsAndCollaborators = forwardRef(({ user, onUserUpdated, setIsLoading }: SkillsAndCollaboratorsProps, ref) => {
  // get required fields & data functions
  const { skills = [], interests = [], highlightedSkills = [], collaborators = [] } = user;
  const { user: authenticatedUser, update } = useAuth();
  const [adminUpdateUserMutation] = useMutation<AdminUpdateUserMutation, AdminUpdateUserMutationVariables>(
    ADMIN_UPDATE_USER_MUTATION,
  );
  const [setCollaborators] = useMutation(SET_COLLABORATORS_MUTATION);
  const [sendCollaboratorEmails] = useMutation(SEND_COLLABORATOR_EMAILS);

  // for skills & interests form
  const interestIds = interests.map(interest => interest.id);
  const skillIds = skills.map(skill => skill.id);
  const highlightedIds = highlightedSkills.map(highlighted => highlighted.id);

  // modal tasks
  const skillsModalRef = useRef<any>();
  const openSkillsModal = () => skillsModalRef.current.open();
  const closeSkillsModal = () => skillsModalRef.current.close();

  const collaboratorsModalRef = useRef<any>();
  const openCollaboratorsModal = () => collaboratorsModalRef.current.open();
  const closeCollaboratorsModal = () => collaboratorsModalRef.current.close();

  useImperativeHandle(ref, () => ({
    openSkillsModal() {
      openSkillsModal();
    },
    openCollaboratorsModal() {
      openCollaboratorsModal();
    },
  }));

  // handling data submission
  const handleSubmitSkills = async (updateData: UserUpdateInput) => {
    try {
      if (authenticatedUser?.isCore) {
        const result = await adminUpdateUserMutation({
          variables: {
            adminUpdateUserInput: updateData,
            adminUpdateUserUserId: user.id,
          },
        });
      } else {
        await update(updateData);
      }
      onUserUpdated();
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmitCollaborators = async (
    userId: MutationSetCollaboratorsArgs['userId'],
    data: MutationSetCollaboratorsArgs['data'],
    closeModal: boolean,
  ) => {
    try {
      await setCollaborators({ variables: { userId, data } });
      onUserUpdated();
      if (closeModal) closeCollaboratorsModal();
    } catch (error) {
      console.error(error);
    }
  };

  const sendEmails = async (emails: string[]) => {
    const userId = authenticatedUser.id;
    try {
      sendCollaboratorEmails({ variables: { userId, emails } });
      closeCollaboratorsModal();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="flex flex-col">
      <Modal ref={skillsModalRef} showCloseButton={true}>
        <SkillsForm
          closeModal={closeSkillsModal}
          selectedInterestIds={interestIds}
          selectedHighlightedSkillIds={highlightedIds}
          selectedSkillIds={skillIds}
          handleSubmit={handleSubmitSkills}
        />
      </Modal>
      <Modal ref={collaboratorsModalRef} showCloseButton={true}>
        <CollaboratorsForm
          sendEmails={sendEmails}
          collaboratorsData={collaborators}
          user={user}
          handleSubmitCollaborators={handleSubmitCollaborators}
        />
      </Modal>
    </div>
  );
});

export default SkillsAndCollaborators;
